import { Input, Select } from '@servicecompressionllc/sc-design-system';
import { Pill, kindCast } from '../controls/OutputValue.js';
import { humanizeKey } from '../definitionEdit.js';
import type { StepEditorProps } from './types.js';

const MAX_REPORT_ON = 10;

export const AgentStepEditor = ({ step, ctx, onChange: put }: StepEditorProps<'agent'>) => {
  const persona = ctx.personas.find((pp) => pp.id === step.personaId);
  const reportOn = step.reportOn ?? [];

  return (
    <>
      <div className="flex flex-wrap gap-3">
        <label className="grid w-64 gap-1 text-xs text-ink-muted">
          Call this step
          <Input
            aria-label="Step name"
            placeholder="e.g. Efficiency review"
            value={step.name ?? ''}
            onChange={(e) => put({ ...step, name: e.target.value || undefined })}
          />
        </label>
        <label className="grid w-72 gap-1 text-xs text-ink-muted">
          Which agent runs it
          <Select
            aria-label="Agent persona"
            searchable={false}
            options={ctx.personas.map((pp) => ({
              value: pp.id,
              label: pp.enableable ? pp.label : `${pp.label} — you don't have access`,
              disabled: !pp.enableable,
            }))}
            value={step.personaId ? [step.personaId] : []}
            onChange={(v) => put({ ...step, personaId: v[0] ?? '' })}
          />
        </label>
      </div>
      {persona && <p className="-mt-1 text-xs text-ink-subtle">{persona.description}</p>}
      {persona && !persona.enableable && (
        <p className="text-xs text-warning">
          This agent reads data you don't have access to, so you won't be able to turn the workflow
          on — someone who has that access will need to enable it.
        </p>
      )}
      <label className="grid min-w-0 gap-1 text-xs text-ink-muted">
        <span>
          <span className="font-semibold text-ink">Goal</span> · what's the job?
        </span>
        <textarea
          aria-label="Goal"
          value={step.task}
          onChange={(e) => put({ ...step, task: e.target.value })}
          rows={3}
          placeholder="e.g. Find the two worst call-out units in OKC East over the last 30 days and figure out what's wrong with each one."
          className="rounded-md border border-outline bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-outline-strong"
        />
      </label>
      <div className="grid min-w-0 gap-1.5">
        <span className="text-xs text-ink-muted">
          <span className="font-semibold text-ink">Answer checklist</span> · the answer isn't
          complete without these (optional)
        </span>
        {reportOn.map((point, i) => (
          <div
            key={i}
            className="flex min-w-0 items-center gap-2.5 rounded-md border border-outline bg-surface px-3 py-2 focus-within:border-outline-strong"
          >
            <span className="w-4 flex-none text-right text-xs text-ink-subtle">
              {String(i + 1)}
            </span>
            <input
              aria-label={`Answer checklist point ${String(i + 1)}`}
              className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none"
              value={point}
              onChange={(e) =>
                put({ ...step, reportOn: reportOn.map((x, j) => (j === i ? e.target.value : x)) })
              }
            />
            <button
              type="button"
              aria-label={`Remove answer checklist point ${String(i + 1)}`}
              className="flex-none text-xs text-ink-subtle hover:text-danger"
              onClick={() => put({ ...step, reportOn: reportOn.filter((_, j) => j !== i) })}
            >
              ✕
            </button>
          </div>
        ))}
        {reportOn.length < MAX_REPORT_ON && (
          <button
            type="button"
            className="rounded-md border border-dashed border-outline px-3 py-2 text-left text-sm text-ink-muted hover:border-outline-strong hover:text-ink"
            onClick={() => put({ ...step, reportOn: [...reportOn, ''] })}
          >
            + Add a point
          </button>
        )}
      </div>
      {/* READ-ONLY: the persona declares its answers, the author never
              invents them. An author can't predict what an agent will find,
              so this is shown, not asked. */}
      {!persona && (
        <p className="text-xs text-ink-subtle">Pick an agent above to see how it answers.</p>
      )}
      {persona && (
        <details>
          <summary className="cursor-pointer text-xs text-ink-muted">
            How it always answers ·{' '}
            <span className="text-ink-subtle">
              {persona.outputs.map((o) => humanizeKey(o.name)).join(' · ')}
            </span>
          </summary>
          <div className="mt-2 grid gap-1.5">
            {persona.outputs.map((o) => (
              <div key={o.name} className="flex min-w-0 flex-wrap items-baseline gap-2">
                <Pill>
                  {humanizeKey(o.name)} · {kindCast(o.kind, o.values)}
                </Pill>
                <span className="min-w-0 flex-1 text-xs text-ink-subtle">
                  {o.label}
                  {o.longForm ? ' Best kept out of messages.' : ''}
                </span>
              </div>
            ))}
            <p className="text-xs text-ink-subtle">
              Later steps use these by this step's name — pick them from “Insert a value”.
            </p>
          </div>
        </details>
      )}
      {/* Outside the disclosure: a missing name blocks enabling, so the nudge
          must not hide behind a closed summary. */}
      {persona && !step.name?.trim() && (
        <p className="text-xs text-warning">
          Name this step above and later steps will be able to use its answers.
        </p>
      )}
    </>
  );
};
